import { createBrowserRouter, Navigate } from 'react-router';
import { AppShell } from './shell/app-shell';
import { RequireSession } from './require-session';
import { HomePage } from '@/features/home/home-page';
import { NotFoundPage, Splash } from '@/features/system/system-pages';
import { RouteError } from '@/features/system/route-error';

export const router = createBrowserRouter([
  {
    hydrateFallbackElement: <Splash />,
    errorElement: <RouteError />,
    children: [
      {
        path: '/login',
        lazy: () => import('@/features/auth/login-page').then((m) => ({ Component: m.LoginPage })),
      },
      {
        path: '/setup',
        lazy: () => import('@/features/auth/setup-page').then((m) => ({ Component: m.SetupPage })),
      },
      {
        path: '/forgot',
        lazy: () => import('@/features/auth/password-pages').then((m) => ({ Component: m.ForgotPage })),
      },
      {
        path: '/reset/:token',
        lazy: () => import('@/features/auth/password-pages').then((m) => ({ Component: m.ResetPage })),
      },
      {
        path: '/p/:token',
        lazy: () => import('@/features/share/public-page').then((m) => ({ Component: m.PublicSharePage })),
      },
      {
        path: '/invite/:token',
        lazy: () => import('@/features/auth/invite-page').then((m) => ({ Component: m.InvitePage })),
      },
      {
        element: <RequireSession />,
        children: [
          {
            path: 'portal',
            lazy: () => import('@/features/portal/portal-shell').then((m) => ({ Component: m.PortalShell })),
            children: [
              {
                path: 'inbox',
                lazy: () =>
                  import('@/features/inbox/conversations').then((m) => ({ Component: m.ConversationsPage })),
              },
              {
                path: 'approvals',
                lazy: () =>
                  import('@/features/portal/portal-pages').then((m) => ({ Component: m.PortalApprovals })),
              },
              {
                path: 'projects',
                lazy: () =>
                  import('@/features/clients/workspace-tabs').then((m) => ({ Component: m.PortalProjects })),
              },
              {
                index: true,
                lazy: () =>
                  import('@/features/portal/portal-pages').then((m) => ({ Component: m.PortalHome })),
              },
              {
                path: 'finance',
                lazy: () =>
                  import('@/features/portal/portal-finance').then((m) => ({ Component: m.PortalFinance })),
              },
              {
                path: 'requests',
                lazy: () =>
                  import('@/features/portal/portal-pages').then((m) => ({ Component: m.PortalRequests })),
              },
              {
                path: 'files',
                lazy: () =>
                  import('@/features/portal/portal-pages').then((m) => ({ Component: m.PortalFiles })),
              },
              {
                path: 'agenda',
                lazy: () =>
                  import('@/features/portal/portal-pages').then((m) => ({ Component: m.PortalAgenda })),
              },
              {
                path: 'projects/:id',
                lazy: () =>
                  import('@/features/portal/portal-pages').then((m) => ({ Component: m.PortalProject })),
              },
            ],
          },
          {
            element: <AppShell />,
            errorElement: <RouteError />,
            children: [
              { index: true, element: <HomePage /> },
              {
                path: 'inbox',
                lazy: () =>
                  import('@/features/inbox/conversations').then((m) => ({ Component: m.ConversationsPage })),
              },
              {
                path: 'requests',
                lazy: () => import('@/features/inbox/inbox-page').then((m) => ({ Component: m.InboxPage })),
              },
              { path: 'projects', element: <Navigate to="/projects/kanban" replace /> },
              {
                path: 'projects/p/:id',
                lazy: () =>
                  import('@/features/projects/project-layout').then((m) => ({ Component: m.ProjectLayout })),
                children: [
                  {
                    index: true,
                    lazy: () =>
                      import('@/features/projects/overview-tab').then((m) => ({ Component: m.OverviewTab })),
                  },
                  {
                    path: 'tasks',
                    lazy: () =>
                      import('@/features/projects/tasks-tab').then((m) => ({ Component: m.ProjectTasksTab })),
                  },
                  {
                    path: 'files',
                    lazy: () =>
                      import('@/features/projects/files-tab').then((m) => ({ Component: m.ProjectFilesTab })),
                  },
                  {
                    path: 'creative',
                    lazy: () =>
                      import('@/features/projects/creative-tab').then((m) => ({
                        Component: m.ProjectCreativeTab,
                      })),
                  },
                  {
                    path: 'activity',
                    lazy: () =>
                      import('@/features/projects/activity-tab').then((m) => ({
                        Component: m.ProjectActivityTab,
                      })),
                  },
                ],
              },
              {
                path: 'projects/:view',
                lazy: () =>
                  import('@/features/projects/projects-page').then((m) => ({ Component: m.ProjectsPage })),
              },
              {
                path: 'calendar',
                lazy: () =>
                  import('@/features/calendar/calendar-page').then((m) => ({ Component: m.CalendarPage })),
              },
              { path: 'creative', element: <Navigate to="/creative/boards" replace /> },
              {
                path: 'creative/boards',
                lazy: () =>
                  import('@/features/creative/boards-page').then((m) => ({ Component: m.BoardsPage })),
              },
              {
                path: 'creative/boards/:id/present',
                lazy: () =>
                  import('@/features/creative/present/present-page').then((m) => ({
                    Component: m.PresentPage,
                  })),
              },
              {
                path: 'creative/boards/:id',
                lazy: () =>
                  import('@/features/creative/board/board-page').then((m) => ({ Component: m.BoardPage })),
              },
              {
                path: 'creative/moodboards',
                lazy: () =>
                  import('@/features/creative/boards-page').then((m) => ({ Component: m.MoodboardsPage })),
              },
              {
                path: 'creative/storyboards',
                lazy: () =>
                  import('@/features/creative/storyboards/storyboards-page').then((m) => ({
                    Component: m.StoryboardsPage,
                  })),
              },
              {
                path: 'creative/storyboards/:id',
                lazy: () =>
                  import('@/features/creative/storyboards/storyboard-page').then((m) => ({
                    Component: m.StoryboardPage,
                  })),
              },
              {
                path: 'creative/storyboards/:id/print',
                lazy: () =>
                  import('@/features/creative/storyboards/print-page').then((m) => ({
                    Component: m.StoryboardPrintPage,
                  })),
              },
              {
                path: 'creative/notes',
                lazy: () =>
                  import('@/features/creative/notes-page').then((m) => ({ Component: m.NotesPage })),
              },
              {
                path: 'creative/notes/:id',
                lazy: () => import('@/features/creative/note-page').then((m) => ({ Component: m.NotePage })),
              },
              {
                path: 'clients',
                lazy: () =>
                  import('@/features/clients/clients-page').then((m) => ({ Component: m.ClientsPage })),
              },
              {
                path: 'clients/:id',
                lazy: () =>
                  import('@/features/clients/client-layout').then((m) => ({ Component: m.ClientLayout })),
                children: [
                  { path: 'creative', element: <Navigate to="boards" replace /> },
                  {
                    path: 'creative/:kind',
                    lazy: () =>
                      import('@/features/clients/client-creative').then((m) => ({
                        Component: m.ClientCreative,
                      })),
                  },
                  ...['projects', 'requests', 'inbox', 'calendar', 'finance'].map((path) => ({
                    path,
                    lazy: () =>
                      import('@/features/clients/workspace-tabs').then((m) => ({
                        Component: m.ClientWorkspaceTab,
                      })),
                  })),
                  {
                    index: true,
                    lazy: () =>
                      import('@/features/clients/overview-tab').then((m) => ({ Component: m.OverviewTab })),
                  },
                  {
                    path: 'brand',
                    lazy: () =>
                      import('@/features/clients/brand-tab').then((m) => ({ Component: m.BrandTab })),
                  },
                  {
                    path: 'files',
                    lazy: () =>
                      import('@/features/clients/files-tab').then((m) => ({ Component: m.FilesTab })),
                  },
                  {
                    path: 'timeline',
                    lazy: () =>
                      import('@/features/clients/timeline-tab').then((m) => ({ Component: m.TimelineTab })),
                  },
                  {
                    path: 'access',
                    lazy: () =>
                      import('@/features/clients/access-tab').then((m) => ({ Component: m.AccessTab })),
                  },
                ],
              },
              {
                path: 'search',
                lazy: () =>
                  import('@/features/search/search-page').then((m) => ({ Component: m.SearchPage })),
              },
              {
                path: 'time',
                lazy: () => import('@/features/time/time-page').then((m) => ({ Component: m.TimePage })),
              },
              { path: 'finance', element: <Navigate to="/finance/overview" replace /> },
              {
                path: 'finance/invoices/:id/print',
                lazy: () =>
                  import('@/features/finance/invoice-print').then((m) => ({ Component: m.InvoicePrintPage })),
              },
              {
                path: 'finance/:tab',
                lazy: () =>
                  import('@/features/finance/finance-page').then((m) => ({ Component: m.FinancePage })),
              },
              {
                path: 'reports',
                lazy: () =>
                  import('@/features/reports/reports-page').then((m) => ({ Component: m.ReportsPage })),
              },
              {
                path: 'assets',
                lazy: () =>
                  import('@/features/assets/assets-page').then((m) => ({ Component: m.AssetsPage })),
              },
              {
                path: 'team',
                lazy: () => import('@/features/team/team-page').then((m) => ({ Component: m.TeamPage })),
              },
              {
                path: 'settings',
                lazy: () =>
                  import('@/features/settings/settings-layout').then((m) => ({
                    Component: m.SettingsLayout,
                  })),
                children: [
                  { index: true, element: <Navigate to="account" replace /> },
                  {
                    path: 'account',
                    lazy: () =>
                      import('@/features/settings/account-settings').then((m) => ({
                        Component: m.AccountSettings,
                      })),
                  },
                  {
                    path: 'organization',
                    lazy: () =>
                      import('@/features/settings/organization-settings').then((m) => ({
                        Component: m.OrganizationSettings,
                      })),
                  },
                  {
                    path: 'members',
                    lazy: () =>
                      import('@/features/settings/members-settings').then((m) => ({
                        Component: m.MembersSettings,
                      })),
                  },
                  {
                    path: 'workflow',
                    lazy: () =>
                      import('@/features/settings/workflow-settings').then((m) => ({
                        Component: m.WorkflowSettings,
                      })),
                  },
                  {
                    path: 'finance',
                    lazy: () =>
                      import('@/features/finance/finance-settings').then((m) => ({
                        Component: m.FinanceSettings,
                      })),
                  },
                  {
                    path: 'files',
                    lazy: () =>
                      import('@/features/settings/files-settings').then((m) => ({
                        Component: m.FilesSettings,
                      })),
                  },
                  {
                    path: 'templates',
                    lazy: () =>
                      import('@/features/settings/templates-settings').then((m) => ({
                        Component: m.TemplatesSettings,
                      })),
                  },
                  {
                    path: 'services',
                    lazy: () =>
                      import('@/features/settings/services-settings').then((m) => ({
                        Component: m.ServicesSettings,
                      })),
                  },
                  {
                    path: 'notifications',
                    lazy: () =>
                      import('@/features/notifications/notification-settings').then((m) => ({
                        Component: m.NotificationSettings,
                      })),
                  },
                  {
                    path: 'appearance',
                    lazy: () =>
                      import('@/features/settings/appearance-settings').then((m) => ({
                        Component: m.AppearanceSettings,
                      })),
                  },
                  { path: ':section', element: <Navigate to="/settings/account" replace /> },
                ],
              },
              { path: '*', element: <NotFoundPage /> },
            ],
          },
        ],
      },
    ],
  },
]);
